import React, { useEffect, useState, useRef } from 'react';
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { ActiveChannelInterface } from './../../interfaces/activeChannelInterface';
import './styles.css';
import MessageComponent from './MessageComponent';
import MessageListComponent from './MessageListComponent';
import MessageInputComponent from './MessageInputComponent';
import ChannelHeaderComponent from './ChannelHeader';
import { getDateFromTimestamp } from '../getDateFromTimestamp';
import { NotificationInterface } from './../../interfaces/notificationInterface';
import EditMessageModal from './EditMessageModal';
import ReportMessageModal from './../../components/ReportModal/ReportMessageModal';
import { checkIfUserIsBlocked } from '../../utils/checkIfUserIsBlocked';
import Message from 'anchor-ui/message';
import chatBotImg from './../../assets/chatbot.jpg';
import AdminBadge from 'anchor-ui/admin-badge';
import Button from 'anchor-ui/button';
import { UserSettingsInterface } from '../../interfaces/userSettingsInterface';
import { ReplyMessageInterface } from '../../interfaces/replyMessageInterface';
import { ConversationInterface } from '../../interfaces/conversationInterface';
import { isGroupConversation } from '../../utils/isGroupConversationCheck';
import GoogleAdSense from '../GoogleAdSense';
import {
  checkIfUserHasPermissions,
  checkIfUserIsAdmin,
  checkIfUserIsModerator
} from '../../utils/checkIfUserIsAdmin';
import { getProfileFromMetadata } from '../../utils/updateMetadata';
import AudioChannelHeader from '../AudioChannelHeader';
import { checkUserHasSubscription, checkUserIsRoyal, checkUserIsVIPOrRoyal } from '../../utils/checkSubscription';
import { InactivityWarningDialog } from './InactivityWarningDialog';
import { ChatBotMessages } from '../../utils/ChatBotMessages';
import { getBackgroundImage, getSecondaryColor } from '../../utils/activeColor';

interface ChatComponentProps {
  currentChat: ActiveChannelInterface;
  setCurrentChat: React.Dispatch<React.SetStateAction<any>>;
  conversations: any;
  loggedInUser: CometChat.User | null;
  loadIncomingFriendRequests: () => void;
  loadOutgoingFriendRequests: () => void;
  setNotifications: React.Dispatch<React.SetStateAction<NotificationInterface[] | null>>;
  blockedUsers: CometChat.User[];
  handleBlockUser: (userId: string) => void;
  friends: CometChat.User[];
  hasSentFriendRequest: (user: CometChat.User) => boolean;
  hasReceivedFriendRequest: (user: CometChat.User) => boolean;
  handleAddOrRemoveFriend: (friend: CometChat.User) => void;
  setUserToReport: (userToReport: { user: CometChat.User, guid?: string } | null) => void;
  setAlert: React.Dispatch<React.SetStateAction<{ message: string; type: string; } | null>>;
  messages: CometChat.BaseMessage[];
  setMessages: React.Dispatch<React.SetStateAction<CometChat.BaseMessage[]>>;
  leftMenuOpen: boolean;
  setLeftMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  rightMenuOpen: boolean;
  setRightMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  unreadCount: { [id: string]: { nr: number, chat: ActiveChannelInterface } };
  groupMembers?: CometChat.GroupMember[];
  chatWithUser?: CometChat.User;
  showLoadMostRecentMsgBtn: boolean;
  setShowLoadMostRecentMsgBtn: React.Dispatch<React.SetStateAction<boolean>>;

  handleMuteUser: (user: CometChat.User) => void;
  handleKickUser: (user: CometChat.User, group: CometChat.Group) => void;
  handleLogoutKickUser: (user: CometChat.User) => void;
  handleBanUser: (user: CometChat.User) => void;
  handleDeleteMessageAsAdmin: (message: CometChat.BaseMessage) => void;
  userSettings: UserSettingsInterface;
  handleRemoveConversation: (conversation: CometChat.Conversation | CometChat.Group) => void;
  setUserToWarn: (user: CometChat.User) => void;
  setMessageToForward: (message: CometChat.BaseMessage) => void;
  handleSendImage: (event: React.ChangeEvent<HTMLInputElement>, receiver: string, isGroup: boolean, isForwarded?: boolean) => void;

  inputRef: React.RefObject<HTMLInputElement>;
  commandMenuOpen: boolean;
  setCommandMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  messageToReply: ReplyMessageInterface | null;
  setMessageToReply: React.Dispatch<React.SetStateAction<ReplyMessageInterface | null>>;
  handleSendMessage: (event: any, receiver: string, isGroup: boolean, messageText?: string) => void;
  isFetching: boolean;
  fetchMessages: () => void;
  buildMessagesRequest: (fetchFromTimestamp: number, limit: number, currentChat: any) => CometChat.MessagesRequest;

  showLoadMoreBtn: boolean;
  setShowLoadMoreBtn: React.Dispatch<React.SetStateAction<boolean>>;

  filterValidMessages: (
    timestamp: number,
    messageList: CometChat.BaseMessage[],
    messages: CometChat.BaseMessage[],
    currentChat: any,
    checkDuplicate?: boolean
  ) => CometChat.BaseMessage[];
  setActiveTabIndex: (index: number) => void;

  startedTyping: boolean;
  setStartedTyping: React.Dispatch<React.SetStateAction<boolean>>;
  setTypingNotification: React.Dispatch<React.SetStateAction<CometChat.TypingIndicator>>;

  usersTyping: { name: string; typingStarted: number; }[] | null;
  setUsersTyping: React.Dispatch<React.SetStateAction<{ name: string; typingStarted: number; }[] | null>>;

  ongoingCall: CometChat.Call | null;
  setOngoingCall: React.Dispatch<React.SetStateAction<CometChat.Call | null>>;
  outgoingCall: { call: CometChat.Call, receiver: CometChat.User } | null;
  setOutgoingCall: React.Dispatch<React.SetStateAction<{ call: CometChat.Call, receiver: CometChat.User } | null>>;

  handleInitiateCall: (receiver: CometChat.User) => void;
  handleTerminateCall: () => void;

  setOpenInactivityKickDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleChangeChannel: (channel: ActiveChannelInterface) => void;
  channels: CometChat.Group[];
  onlineMembersCount: { [key: string]: number } | null;

  handleUnflagAdultOnly: (user: CometChat.User) => void;
}

const ChatComponent: React.FC<ChatComponentProps> = ({
  currentChat,
  setCurrentChat,
  conversations,
  loggedInUser,
  loadIncomingFriendRequests,
  loadOutgoingFriendRequests,
  blockedUsers,
  handleBlockUser,
  friends,
  hasSentFriendRequest,
  hasReceivedFriendRequest,
  handleAddOrRemoveFriend,
  setUserToReport,
  setAlert,

  messages,
  setMessages,

  leftMenuOpen,
  setLeftMenuOpen,
  rightMenuOpen,
  setRightMenuOpen,
  unreadCount,

  groupMembers,
  chatWithUser,

  showLoadMostRecentMsgBtn,
  setShowLoadMostRecentMsgBtn,

  handleMuteUser,
  handleKickUser,
  handleLogoutKickUser,
  handleBanUser,
  handleDeleteMessageAsAdmin,

  userSettings,

  handleRemoveConversation,
  setUserToWarn,
  setMessageToForward,

  inputRef,
  commandMenuOpen,
  setCommandMenuOpen,
  messageToReply,
  setMessageToReply,
  handleSendMessage,
  handleSendImage,

  isFetching,
  fetchMessages,
  buildMessagesRequest,

  showLoadMoreBtn,
  setShowLoadMoreBtn,

  filterValidMessages,
  setActiveTabIndex,

  startedTyping,
  setStartedTyping,
  setTypingNotification,

  usersTyping,
  setUsersTyping,

  ongoingCall,
  setOngoingCall,
  outgoingCall,
  setOutgoingCall,

  handleInitiateCall,
  handleTerminateCall,

  setOpenInactivityKickDialog,
  handleChangeChannel,
  channels,
  onlineMembersCount,

  handleUnflagAdultOnly,
}) => {
  // ChatBotmsg countdown
  const chatBotInterval = useRef<NodeJS.Timeout | null>(null);

  const isVip = checkUserIsVIPOrRoyal(loggedInUser?.getRole() ?? '');
  const isRoyal = checkUserIsRoyal(loggedInUser?.getRole() ?? '');

  // Inactivity countdown
  const warningCountdownInterval = useRef<NodeJS.Timeout | null>(null);
  const warningCountdownTime = 10 * 60 * 1000; // 10 minutes in milliseconds
  const kickCountdownInterval = useRef<NodeJS.Timeout | null>(null);
  const kickCountdownTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  const [inactivityDialogOpen, setInactivityDialogOpen] = useState(false);

  const [editMessage, setEditMessage] = useState<CometChat.TextMessage | null>(null);
  const [messageToReport, setMessageToReport] = useState<CometChat.BaseMessage | null>(null);

  const [emojiMenuOpen, setEmojiMenuOpen] = useState(false);

  const messageEndRef = useRef<HTMLDivElement | null>(null);

  const loadIncomingFriendRequestsRef = useRef(loadIncomingFriendRequests);
  const loadOutgoingFriendRequestsRef = useRef(loadOutgoingFriendRequests);

  const channelIdsWithWelcomeMsg = [
    "admin_chat",
    "familymeeting",
    "gaylife",
    "gewaagd",
    "jongerenchat",
    "lesbox",
    "ongewenst",
    "sm_dungeon",
    "zaadbunker",
    "pikant"
  ];

  const publicChannelIds = channels.map(channel => channel.getGuid());

  // Fetch older messages for the current chat
  const fetchOlderMessages = async () => {
    const fetchFromTimestamp = messages[0]?.getSentAt() ?? 0;
    const scrollToId = messages[0]?.getId() + '';
    let messagesRequest = buildMessagesRequest(fetchFromTimestamp, 50, currentChat);

    try {
      const messageList = await messagesRequest.fetchPrevious();
      if (messageList.length < 50) setShowLoadMoreBtn(false);

      const validMessages = filterValidMessages(0, messageList, messages, loggedInUser, true);
      const updatedMessages = [...validMessages, ...messages];

      // If the updatedMessages array is over 150 messages, remove the last 50 messages
      if (updatedMessages.length > 150) {
        updatedMessages.splice(150, 50); // Remove older messages
        setShowLoadMostRecentMsgBtn(true);
      }

      setMessages(updatedMessages);

      // Scroll to the previous oldest message fetched
      const previousOldestMsg = document.getElementById(scrollToId);
      if (previousOldestMsg) previousOldestMsg.scrollIntoView();
    } catch (error) {
      console.error('Message fetching failed with error:', error);
      setAlert({ message: 'Berichten konden niet worden opgehaald', type: 'error' });
    }
  };

  useEffect(() => {
    loadIncomingFriendRequestsRef.current = loadIncomingFriendRequests;
    loadOutgoingFriendRequestsRef.current = loadOutgoingFriendRequests;
  }, [loadIncomingFriendRequests, loadOutgoingFriendRequests]);

  const handleDeleteMessage = (messageId: string) => {
    const metadata = getProfileFromMetadata(loggedInUser!);
    const isMuted = metadata.muted;

    if (isMuted) {
      setMessages(prevMessages => prevMessages.filter(message => message.getId() + '' !== messageId));
      return;
    }

    CometChat.deleteMessage(messageId).then(
      () => {
        setMessages(prevMessages => prevMessages.filter(message => message.getId() + '' !== messageId));
      },
      error => {
        console.error('Message deleting failed with error:', error);
        setAlert({ message: 'Bericht kon niet worden verwijderd', type: 'error' });
      }
    );
  }

  const getConversationFromMessage = (message: CometChat.BaseMessage) => {
    return conversations.find((conversation: ConversationInterface) => {
      if (isGroupConversation(conversation)) return false;
      const privateConversation = conversation.conversation as CometChat.Conversation;
      return privateConversation.getConversationId() === message.getReceiverId();
    });
  };

  const isAdultChannel = (groupName: string): boolean => {
    switch (groupName) {
      case "Gewaagd":
      case "Ongewenst":
      case "SM Dungeon":
      case "Pikant":
      case "Bi-Box":
      case "Lesbox":
      case "Gaylife":
      case "50+":
      case "Oldies":
      case "60, 70, 80 Jaren":
        return true;
      default:
        return false;
    }
  };

  const startChatBotTimer = (
    currentChat: ActiveChannelInterface,
    onlineMembersCount: { [key: string]: number } | null
  ) => {
    // Always clear any existing interval first
    if (chatBotInterval.current) {
      clearInterval(chatBotInterval.current);
      chatBotInterval.current = null;
    }

    const channelId = currentChat.id;

    // Early return if not a group or not a public channel
    if (!channelId || !currentChat.isGroup) return;
    const currentChatIsPublic = publicChannelIds.includes(channelId);
    if (!currentChatIsPublic) return;

    chatBotInterval.current = setInterval(() => {
      // Double-check that we're still in a public channel when the interval fires
      const currentChatIsStillPublic = publicChannelIds.includes(currentChat.id);
      if (!currentChatIsStillPublic) {
        clearInterval(chatBotInterval.current!);
        chatBotInterval.current = null;
        return;
      }

      // Check if there is already a chatbot message in the array
      setMessages((prevMessages) => {
        // Find the most recent chatbot message
        const lastChatbotMessage = [...prevMessages].reverse().find(
          (message) => message.getSender().getUid() === "chatbot"
        );

        // If there is a recent chatbot message, check if it was sent within the last 14 minutes
        if (lastChatbotMessage) {
          const lastMessageTime = lastChatbotMessage.getSentAt();
          const currentTime = Math.floor(new Date().getTime() / 1000);
          const timeDifference = currentTime - lastMessageTime;

          // If less than 24 minutes have passed (14 * 60 = 840 seconds), don't add a new message
          if (timeDifference < 24 * 60) {
            return prevMessages; // Return unchanged messages
          }
        }

        // Continue with adding a new chatbot message
        const currentUserRole = loggedInUser?.getRole() ?? "";
        const isVip = checkUserIsVIPOrRoyal(currentUserRole);
        const isRoyal = checkUserIsRoyal(currentUserRole);

        let randomMsg: any = null;

        // Filter out messages that shouldn't be shown
        do {
          randomMsg = ChatBotMessages[Math.floor(Math.random() * ChatBotMessages.length)];
        } while (
          currentUserRole === randomMsg.subscriptionType ||
          (randomMsg.subscriptionType === "premium" ||
            (randomMsg.subscriptionType === "vip") && isVip) ||
          (randomMsg.subscriptionType && isRoyal) ||
          channelId === randomMsg.channelId ||
          (randomMsg.type === "adultWebsite" && !isAdultChannel(currentChat.name))
        );

        const chatBotMessage = new CometChat.TextMessage(
          channelId,
          randomMsg.message,
          currentChat.isGroup ? CometChat.RECEIVER_TYPE.GROUP : CometChat.RECEIVER_TYPE.USER
        );

        const featuredChannel = channels.find(channel => channel.getGuid() === randomMsg.channelId) as any;

        const channelOnlineMembersCount = randomMsg.channelId
          ? onlineMembersCount?.[randomMsg.channelId] ?? Number(featuredChannel?.onlineMembersCount)
          : 1;

        chatBotMessage.setData({
          chatbotData: {
            type: randomMsg.type,
            channelId: randomMsg.channelId + "",
            channelOnlineMembersCount,
            url: randomMsg.url + "",
          }
        });

        const chatBotUser = new CometChat.User("chatbot");
        chatBotUser.setName("Chatbot");
        chatBotUser.setAvatar(chatBotImg);

        chatBotMessage.setSender(chatBotUser);
        chatBotMessage.setSentAt(Math.floor(new Date().getTime() / 1000));

        // Add the new message to the array
        return [...prevMessages, chatBotMessage];
      });
    }, 25 * 60 * 1000); // 25 minutes in milliseconds
  };

  const checkIfSenderMuted = (message: CometChat.BaseMessage) => {
    const profile = getProfileFromMetadata(message.getSender());
    return profile.muted;
  }

  useEffect(() => {
    // Fetch messages for the current chat when the chat is opened
    fetchMessages();

    // Reset the users typing array when a new chat is opened
    setUsersTyping([]);
    // Set the typing notification for the current chat
    setTypingNotification(currentChat.isGroup ?
      new CometChat.TypingIndicator(currentChat.id, CometChat.RECEIVER_TYPE.GROUP)
      : new CometChat.TypingIndicator(chatWithUser?.getUid(), CometChat.RECEIVER_TYPE.USER));

    const listenerId = 'message-listener';

    CometChat.addMessageListener(
      listenerId,
      new CometChat.MessageListener({
        onTypingStarted: (typingIndicator: CometChat.TypingIndicator) => {
          const typingUserProfile = getProfileFromMetadata(typingIndicator.getSender());
          // If the typing indicator is for the current chat, add the user to the usersTyping array
          if (!typingUserProfile.muted && (
            typingIndicator.getSender().getUid() === currentChat.receiverId ||
            currentChat.id === typingIndicator.getReceiverId())
          ) {
            setUsersTyping(prevUsersTyping => {
              if (!prevUsersTyping) return [{ name: typingIndicator.getSender().getName(), typingStarted: Date.now() }];

              const senderUid = typingIndicator.getSender().getUid();
              const senderName = typingIndicator.getSender().getName();

              // If the chat is a group chat, check if the user is a member of the group
              // and only add the user to the usersTyping array if they are a member
              if (currentChat.isGroup) {
                const isMember = groupMembers?.some(member => member.getUid() === senderUid);

                // if they are not a member, remove them from the array
                if (!isMember) {
                  return prevUsersTyping.filter(user => user.name !== senderName);
                }
              }

              const userIsAlreadyTyping = prevUsersTyping.find(user => user.name === senderName);
              if (userIsAlreadyTyping) return prevUsersTyping;

              return [...prevUsersTyping, { name: typingIndicator.getSender().getName(), typingStarted: Date.now() }];
            });
          }
        },
        onTypingEnded: (typingIndicator: CometChat.TypingIndicator) => {
          // If the typing indicator is for the current chat, remove the user from the usersTyping array
          if (typingIndicator.getSender().getUid() === currentChat.receiverId ||
            currentChat.id === typingIndicator.getReceiverId()
          ) {
            setUsersTyping(prevUsersTyping => {
              if (!prevUsersTyping) return prevUsersTyping;

              return prevUsersTyping.filter(user => user.name !== typingIndicator.getSender().getName());
            });
          }
        },
        onTextMessageReceived: (message: CometChat.TextMessage) => {
          // Do not show messages if the current group is premium and the user is not
          // if (
          //   currentChat.isGroup &&
          //   premiumChannelIds.includes(currentChat.id) &&
          //   !checkUserHasSubscription(loggedInUser?.getRole() ?? '')
          // ) {
          //   return;
          // }
          const senderUid = message.getSender().getUid();
          if (senderUid === loggedInUser?.getUid()) return;

          // Check if the message contains any forbidden characters or if name is empty
          const senderName = message.getSender().getName().replace(/[^a-zA-Z0-9]/g, ''); // remove special characters
          if (senderName === "") return;

          if (
            (message.getSender().getUid() === currentChat.id ||
              message.getReceiverId() === currentChat.id ||
              message.getConversationId() === currentChat.id)
            && !showLoadMostRecentMsgBtn &&
            !checkIfUserIsBlocked(blockedUsers, senderUid) &&
            !checkIfSenderMuted(message)
          ) {
            CometChat.markAsRead(message);
            setMessages(prevMessages => [...prevMessages, message]);
          }

          // Get the conversation and update the last message
          const conversation = getConversationFromMessage(message);

          // Update the last message in the conversation
          if (conversation) {
            conversation.lastMsg = message.getText();
            conversation.isMyMsg = message.getSender().getUid() === loggedInUser?.getUid();
          }
        },
        onMediaMessageReceived: (message: CometChat.BaseMessage) => {
          // Do not show messages if the current group is premium and the user is not
          // if (
          //   currentChat.isGroup &&
          //   premiumChannelIds.includes(currentChat.id) &&
          //   !checkUserHasSubscription(loggedInUser?.getRole() ?? '')
          // ) {
          //   return;
          // }
          const senderUid = message.getSender().getUid();

          if (senderUid === loggedInUser?.getUid()) return;

          if ((message.getReceiverId() === currentChat.id ||
            message.getConversationId() === currentChat.id)
            && !showLoadMostRecentMsgBtn &&
            !checkIfUserIsBlocked(blockedUsers, senderUid) &&
            !checkIfSenderMuted(message)
          ) {
            CometChat.markAsRead(message);

            if (!showLoadMostRecentMsgBtn) setMessages(prevMessages => [...prevMessages, message]);
          }

          // Get the conversation and update the last message
          const conversation = getConversationFromMessage(message);

          // Update the last message in the conversation
          if (conversation) {
            conversation.lastMsg = 'Afbeelding';
            conversation.isMyMsg = message.getSender().getUid() === loggedInUser?.getUid();
          }
        },
        onCustomMessageReceived: (message: CometChat.CustomMessage) => {
          let tags = message.getTags();
          if (!tags) tags = (message.getCustomData() as any).tags;

          if (tags) {
            if (tags.includes('friend_request') || tags.includes('reload_requests')) {
              loadIncomingFriendRequestsRef.current();
              return;
            }

            if (tags.includes('reload_requests')) {
              loadOutgoingFriendRequestsRef.current();
              return;
            }
          }

          if (
            message.getType() === 'extension_poll' &&
            !showLoadMostRecentMsgBtn &&
            !checkIfSenderMuted(message)
          ) {
            setMessages(prevMessages => [...prevMessages, message]);
          }
        },
        onMessageDeleted: (message: CometChat.BaseMessage) => {
          setMessages(prevMessages => prevMessages.filter(msg => msg.getId() !== message.getId()));
        },
        onMessageEdited: (message: CometChat.BaseMessage) => {
          setMessages(prevMessages => {
            const updatedMessages = prevMessages.map(msg => {
              if (msg.getId() === message.getId()) {
                return message;
              }
              return msg;
            });
            return updatedMessages;
          });
        },
      })
    );

    return () => {
      CometChat.removeMessageListener(listenerId);
    };
  }, [currentChat]);

  useEffect(() => {
    // Start timer to periodically send chatbot messages
    startChatBotTimer(currentChat, onlineMembersCount);
  }, [currentChat, onlineMembersCount]);

  const startKickCountdown = () => {
    const hasSubscription = checkUserHasSubscription(loggedInUser?.getRole() ?? "");
    if (hasSubscription) return;

    let kickTimeLeft = kickCountdownTime;

    kickCountdownInterval.current = setInterval(() => {
      kickTimeLeft -= 1000; // Decrement timeLeft by 1 second (1000 milliseconds)

      if (kickTimeLeft <= 0) {
        clearInterval(kickCountdownInterval.current as NodeJS.Timeout);
        setCurrentChat({ id: '', name: '', icon: '', isGroup: false });
        setInactivityDialogOpen(false);
        setOpenInactivityKickDialog(true);
      }
    }, 1000); // 1 second interval
  }

  const startWarningCountdown = () => {
    const hasSubscription = checkUserHasSubscription(loggedInUser?.getRole() ?? "");
    if (hasSubscription) return;

    let warningTimeLeft = warningCountdownTime;

    warningCountdownInterval.current = setInterval(() => {
      warningTimeLeft -= 1000; // Decrement timeLeft by 1 second (1000 milliseconds)

      if (warningTimeLeft <= 0) {
        clearInterval(warningCountdownInterval.current as NodeJS.Timeout);
        setInactivityDialogOpen(true);
        startKickCountdown();
      }
    }, 1000); // 1 second interval
  };

  const stopKickCountdown = () => {
    if (kickCountdownInterval.current) {
      clearInterval(kickCountdownInterval.current);
      kickCountdownInterval.current = null;
    }
  };

  const stopWarningCountdown = () => {
    if (warningCountdownInterval.current) {
      clearInterval(warningCountdownInterval.current);
      warningCountdownInterval.current = null;
    }

    stopKickCountdown();
  };

  const resetWarningCountdown = () => {
    if (warningCountdownInterval.current) {
      clearInterval(warningCountdownInterval.current);
      warningCountdownInterval.current = null;
    }

    stopKickCountdown();
    startWarningCountdown();
  };

  // This function sends a message and resets the warning countdown
  const handleSendMessageAndResetWarning = (event: any, receiver: string, isGroup: boolean, messageText?: string) => {
    resetWarningCountdown();
    handleSendMessage(event, receiver, isGroup, messageText);
  };

  // This function sends an image and resets the warning countdown
  const handleSendImageAndResetWarning = (event: React.ChangeEvent<HTMLInputElement>, receiver: string, isGroup: boolean, isForwarded?: boolean) => {
    resetWarningCountdown();
    handleSendImage(event, receiver, isGroup, isForwarded);
  };

  // This function handles the start of typing and resets the warning countdown
  const handleStartTypingAndResetWarning = (startedTyping: boolean) => {
    resetWarningCountdown();
    setStartedTyping(startedTyping);
  }

  useEffect(() => {
    // Clear any existing interval when currentChat changes
    if (warningCountdownInterval.current) {
      clearInterval(warningCountdownInterval.current);
    }

    if (currentChat && currentChat.isGroup) {
      startWarningCountdown();
    }

    // Cleanup function to clear the interval when the component unmounts
    return () => {
      if (warningCountdownInterval.current) {
        clearInterval(warningCountdownInterval.current);
      }
    };
  }, [currentChat?.id]);

  const checkIfMessageContainsForbiddenChars = (message: CometChat.BaseMessage) => {
    if (!(message instanceof CometChat.TextMessage)) return false;
    const textMessage = message as CometChat.TextMessage;
    const invisibleCharacterRegex = /[\u200B\u200C\u200D\u200E\u200F\u202A\u202B\u202C\u202D\u202E\u2060\u2061\u2062\u2063\u2064\u2066\u2067\u2068\u2069\uFEFF\u00AD\u034F\u0300-\u036F\u180E]/;

    let containsForbiddenCharacter = false;
    const forbiddenCharacters = ["𝚛", "𝚑", "𝚊", "𝚝", "𝚒", "𝒞", "ℂ", "ℂ", "ḥ"];

    const forbiddenLinks = ["goo.su/"]

    // Check if the message contains any of the forbidden characters
    forbiddenCharacters.forEach((char) => {
      if (textMessage.getText().includes(char) || forbiddenLinks.some(link => textMessage.getText().includes(link))) {
        containsForbiddenCharacter = true;
      }
    });

    return invisibleCharacterRegex.test(textMessage.getText()) || containsForbiddenCharacter;
  };

  // Separate function to determine message list class names
  const getMessageListClasses = () => {
    const classes = ['message-list'];

    // Only apply special styling if user is VIP or Royal
    if (isVip || isRoyal) {
      // Add class for background image
      if (userSettings.bgImg && userSettings.bgImg !== "default") {
        classes.push('message-list-big');
      }

      // Add dark mode class for custom secondary colors
      if (userSettings?.secondaryColor &&
        userSettings?.secondaryColor !== "white" &&
        (userSettings.bgImg && userSettings.bgImg === "default")) {
        classes.push('message-list-dm');
      }
    }

    // These padding classes apply to all users regardless of status
    if (emojiMenuOpen) {
      classes.push('emoji-padding');
    } else if (commandMenuOpen) {
      classes.push('command-padding');
    } else if (messageToReply) {
      classes.push('reply-padding');
    }

    return classes.join(' ');
  };

  // Separate function for filter styles
  const getFilterStyle = () => {
    // Only apply filters if user is VIP or Royal
    if (isVip || isRoyal) {
      if (getSecondaryColor() === "white" ||
        (userSettings.bgImg && userSettings.bgImg !== "default")) {
        return "";
      }
      return 'invert(1)';
    }

    // Default case - no filter for regular users
    return "";
  };

  const getBotWelcomeMessage = (chatId: string) => {

    const getAdultWarning = () => {
      return (
        <>
          <b>🚨 Let op! 🚨</b> Deze chat is <b>18+</b> 🔞<br />
        </>
      )
    }

    switch (chatId) {
      case "admin_chat":
        return (
          <span className='chatbot-msg'>
            <b>Let op:</b> Deze chatroom is uitsluitend bedoeld voor het stellen van vragen over problemen of ondersteuning. Gebruik deze ruimte niet voor gewone gesprekken. Bedankt voor je begrip!
          </span>
        );
      case "familymeeting":
        return (
          <span className="chatbot-msg">
            📢 Welkom in Family Meeting! 🏡<br />
            ⚠️ Dit kanaal is vanaf 16 jaar, waardoor 18+ onderwerpen, profiel, en namen hier niet thuis horen.<br />
            ⚠️ Heb je last van iemand? Blokkeer deze chatter dan. <br />
            ⚠️ Zie je iets wat niet door de beugel kan? Meld het (het liefst met screenshot) in het Admin Hulp kanaal.<br />
            <br />
            Veel plezier, en laten we het een fijne en veilige omgeving houden voor iedereen! 🪻
          </span>
        );
      case "jongerenchat":
        return (
          <span className="chatbot-msg">
            👋 Welkom in Jongeren Chat! 🎉<br />
            ⚠️ Dit kanaal is bedoeld voor jongeren van 16 t/m 25 jaar.<br />
            ⚠️ Houd het gezellig: 18+ content, provocerende namen of ongepaste profielfoto’s horen hier niet thuis.<br />
            ⚠️ Heb je last van iemand? Blokkeer die persoon via hun profiel.<br />
            ⚠️ Zie je iets wat echt niet oké is? Meld het (liefst met screenshot) in het Admin Hulp kanaal.<br />
            <br />
            Veel chatplezier en laten we er samen een toffe, respectvolle plek van maken! 💬✨
          </span>
        );
      case "lesbox":
        return (
          <span className='chatbot-msg'>
            {getAdultWarning()}
            <hr />
            🎉 Welkom in de chatroom Lesbox! 🌸<br />
            Deze ruimte is speciaal voor vrouwen om in een veilige en respectvolle omgeving te praten. <br />
            <br />
            ⚠️ <b>Huisregels:</b><br />
            Respecteer elkaar en houd het gezellig.<br />
            Voel je vrij om vragen te stellen of gesprekken te starten! 😊<br />
            Veel plezier en laten we er samen iets moois van maken! 🌈<br />
          </span>
        );
      case "gaylife":
      case "gewaagd":
      case "zaadbunker":
      case "sm_dungeon":
      case "pikant":
      case "bibox":
        return (
          <span className='chatbot-msg'>
            {getAdultWarning()}
          </span>
        );
      default:
        return null;
    }
  }

  return (
    <div className="chat-component">
      <InactivityWarningDialog
        inactivityDialogOpen={inactivityDialogOpen}
        setInactivityDialogOpen={setInactivityDialogOpen}
        setCurrentChat={setCurrentChat}
        stopWarningCountdown={stopWarningCountdown}
        resetWarningCountdown={resetWarningCountdown}
      />

      <EditMessageModal
        editMessage={editMessage}
        setEditMessage={setEditMessage}
        setMessages={setMessages}
        setAlert={setAlert}
      />
      <ReportMessageModal
        messageToReport={messageToReport}
        setMessageToReport={setMessageToReport}
        setAlert={setAlert}
      />

      <AudioChannelHeader
        ongoingCall={ongoingCall}
        setOngoingCall={setOngoingCall}
        outgoingCall={outgoingCall}
        setOutgoingCall={setOutgoingCall}
      />

      <ChannelHeaderComponent
        blockedUsers={blockedUsers}
        handleBlockUser={handleBlockUser}
        currentChat={currentChat}
        setCurrentChat={setCurrentChat}
        friends={friends}
        hasSentFriendRequest={hasSentFriendRequest}
        hasReceivedFriendRequest={hasReceivedFriendRequest}
        handleAddOrRemoveFriend={handleAddOrRemoveFriend}
        setUserToReport={setUserToReport}
        leftMenuOpen={leftMenuOpen}
        setLeftMenuOpen={setLeftMenuOpen}
        rightMenuOpen={rightMenuOpen}
        setRightMenuOpen={setRightMenuOpen}
        unreadCount={unreadCount}
        userSettings={userSettings}
        handleRemoveConversation={handleRemoveConversation}
        loggedInUserIsAdmin={checkIfUserHasPermissions(loggedInUser!)}

        outgoingCall={outgoingCall}
        ongoingCall={ongoingCall}

        handleInitiateCall={handleInitiateCall}
        handleTerminateCall={handleTerminateCall}
      />

      <div
        className={getMessageListClasses()}
        style={{
          filter: getFilterStyle(),
          backgroundImage: getBackgroundImage(userSettings.bgImg, loggedInUser?.getRole() ?? ""),
        }}
      >
        <div
          style={{
            filter: getFilterStyle(),
            height: "100%",
          }}
        >
          <GoogleAdSense
            adSlot="8285598752"
            className="chat-top-rectangle"
            isRoyal={checkUserIsRoyal(loggedInUser?.getRole() ?? "")}
            isFixed
          />

          <GoogleAdSense
            adSlot="5430939007"
            style={{ width: "300px", height: "60px" }}
            className="chat-top-rectangle-mobile"
            isRoyal={checkUserIsRoyal(loggedInUser?.getRole() ?? "")}
            isFixed
          />

          <MessageListComponent>
            {showLoadMoreBtn && messages.length > 0 && (
              <Button
                inverted
                onClick={fetchOlderMessages}
                style={{ marginTop: "150px" }}
              >
                Laad Meer Berichten
              </Button>
            )}

            {channelIdsWithWelcomeMsg.includes(currentChat.id) && !isFetching && (
              <Message
                avatar={chatBotImg}
                body={getBotWelcomeMessage(currentChat.id)}
                createdAt={
                  <span className='chatbot-msg'>
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>
                }
                username={
                  <>
                    <span>ChatBot </span>
                    <AdminBadge text="bot" />
                  </>
                }
              />
            )}

            {/* {(currentChat.showPinnedMessages ? pinnedMessages : messages) */}
            {messages.map((message, index) => (
              checkIfMessageContainsForbiddenChars(message) ? null : (
                <MessageComponent
                  key={message.getId()}
                  message={message}
                  myMessage={loggedInUser !== null && message.getSender().getUid() === loggedInUser.getUid()}
                  showMessageSeparator={index === 0 || getDateFromTimestamp(message.getSentAt()) !== getDateFromTimestamp((messages)[index - 1].getSentAt())}
                  isBlocked={checkIfUserIsBlocked(blockedUsers, message.getSender().getUid())}
                  handleDeleteMessage={handleDeleteMessage}
                  setEditMessage={setEditMessage}
                  setMessageToReport={setMessageToReport}
                  handleBlockUser={handleBlockUser}
                  groupMembers={groupMembers}
                  loggedInUser={loggedInUser ?? undefined}
                  setCurrentChat={setCurrentChat}
                  setAlert={setAlert}

                  isAdmin={checkIfUserIsAdmin(message.getSender())}
                  isModerator={checkIfUserIsModerator(message.getSender())}
                  loggedInUserIsAdmin={checkIfUserIsAdmin(loggedInUser!)}
                  loggedInUserIsModerator={checkIfUserIsModerator(loggedInUser!)}

                  handleMuteUser={handleMuteUser}
                  handleKickUser={handleKickUser}
                  handleLogoutKickUser={handleLogoutKickUser}
                  handleBanUser={handleBanUser}
                  handleDeleteMessageAsAdmin={handleDeleteMessageAsAdmin}
                  currentChat={currentChat}
                  userSettings={userSettings}
                  setMessageToReply={setMessageToReply}
                  setUserToWarn={setUserToWarn}
                  setMessageToForward={setMessageToForward}
                  setActiveTabIndex={setActiveTabIndex}

                  handleChangeChannel={handleChangeChannel}
                  channels={channels}

                  handleUnflagAdultOnly={handleUnflagAdultOnly}
                />
              )))}
            {/* Display a message from the ChatBot if the chat is a private chat and the user has not sent any messages yet: */}
            {(!currentChat.isGroup && chatWithUser) && messages.length === 0 && !isFetching && (
              <Message
                avatar={chatBotImg}
                body={
                  <span className='chatbot-msg'>
                    Deel nooit gegevens met vreemden en open nooit onbekende urls. ChatPlaza en moderators zullen nooit vragen naar accountgegevens of vragen om betalingen te doen. Kom je iets verdachts tegen? Rapporteer het bericht en/of de gebruiker.
                  </span>
                }
                createdAt={
                  <span className='chatbot-msg'>
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>
                }
                username={
                  <>
                    <span>ChatBot </span>
                    <AdminBadge text="bot" />
                    <small> Alleen zichtbaar voor jou</small>
                  </>
                }
              />
            )}

            {showLoadMostRecentMsgBtn && messages.length > 0 && (
              <Button
                inverted
                onClick={fetchMessages}
              >
                Laad Recente Berichten
              </Button>
            )}
            <div ref={messageEndRef} />
          </MessageListComponent>
        </div>

      </div>

      <MessageInputComponent
        inputRef={inputRef}
        handleSendMessage={handleSendMessageAndResetWarning}
        handleSendImage={handleSendImageAndResetWarning}
        groupMembers={groupMembers}
        chatWithUser={chatWithUser}

        emojiMenuOpen={emojiMenuOpen}
        setEmojiMenuOpen={setEmojiMenuOpen}
        setCommandMenuOpen={setCommandMenuOpen}
        currentChat={currentChat}

        messageToReply={messageToReply}
        setMessageToReply={setMessageToReply}

        startedTyping={startedTyping}
        setStartedTyping={handleStartTypingAndResetWarning}

        usersTyping={usersTyping}
        userSettings={userSettings}

        loggedInUser={loggedInUser!}
      />
    </div>
  );
};

export default ChatComponent; 